/**
 * DB 행 타입. supabase/migrations 의 스키마와 1:1로 대응한다.
 * 스키마를 바꾸면 이 파일도 함께 바꾼다.
 */

export type ProjectType = 'new_store' | 'renewal' | 'equipment_change' | 'other';
export type ProjectStatus = 'active' | 'on_hold' | 'done' | 'archived';
export type MemberRole = 'owner' | 'designer' | 'reviewer' | 'viewer';
export type VersionStatus =
  | 'draft'
  | 'in_review'
  | 'changes_requested'
  | 'approved'
  | 'published'
  | 'archived';
export type JobStatus =
  | 'queued'
  | 'processing'
  | 'completed'
  | 'completed_with_warnings'
  | 'failed';

export interface Project {
  id: string;
  name: string;
  project_type: ProjectType;
  status: ProjectStatus;
  store_code: string | null;
  store_name: string | null;
  description: string | null;
  starts_on: string | null;
  ends_on: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface ProjectMember {
  project_id: string;
  email: string;
  role: MemberRole;
  invited_by: string | null;
  created_at: string;
}

export interface Drawing {
  id: string;
  project_id: string;
  name: string;
  drawing_type: string;
  floor_name: string | null;
  zone_tags: string[];
  unit: 'mm' | 'cm' | 'm';
  current_branch_id: string | null;
  published_version_id: string | null;
  thumbnail_path: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface DrawingVersion {
  id: string;
  drawing_id: string;
  branch_id: string;
  version_number: number;
  name: string;
  change_summary: string | null;
  status: VersionStatus;
  entity_count: number;
  created_by: string;
  created_at: string;
}

/** cad_app_user 행 (관리 화면용). 시스템 역할은 AppRole(boot/session.ts) 참조. */
export interface AppUserRow {
  email: string;
  name: string | null;
  role: import('../boot/session').AppRole;
  dept: string | null;
  store_code: string | null;
  active: boolean;
  created_at: string;
}

/** cad_my_review_queue 뷰 행 */
export interface ReviewQueueItem {
  version_id: string;
  version_number: number;
  status: VersionStatus;
  created_at: string;
  created_by: string;
  drawing_id: string;
  drawing_name: string;
  project_id: string;
  project_name: string;
}

/* ---------------- 화면용 파생 타입 ---------------- */

/** 프로젝트 목록 카드에 필요한 집계 */
export interface ProjectSummary extends Project {
  drawing_count: number;
  my_role: MemberRole | null;
  pending_review_count: number;
}

/** 도면 목록 행 */
export interface DrawingRow extends Drawing {
  is_favorite: boolean;
  latest_version: DrawingVersion | null;
}

/* ---------------- 표시 라벨 ---------------- */

export const PROJECT_TYPE_LABEL: Record<ProjectType, string> = {
  new_store: '신규 출점',
  renewal: '리뉴얼',
  equipment_change: '장비 교체',
  other: '기타',
};

export const PROJECT_STATUS_LABEL: Record<ProjectStatus, string> = {
  active: '진행 중',
  on_hold: '보류',
  done: '완료',
  archived: '보관',
};

export const MEMBER_ROLE_LABEL: Record<MemberRole, string> = {
  owner: '프로젝트 관리자',
  designer: '설계자',
  reviewer: '검토자',
  viewer: '열람자',
};

/**
 * 버전 상태 표시.
 * 접근성 요구(기획서 16장): 색상만으로 구분하지 않도록 아이콘을 함께 제공한다.
 */
export const VERSION_STATUS_META: Record<
  VersionStatus,
  { label: string; icon: string; color: string }
> = {
  draft: { label: '작성 중', icon: '✎', color: 'var(--one-text-muted)' },
  in_review: { label: '검토 요청됨', icon: '◷', color: 'var(--sc-warn)' },
  changes_requested: { label: '수정 요청됨', icon: '↩', color: 'var(--sc-danger)' },
  approved: { label: '승인됨', icon: '✓', color: 'var(--sc-ok)' },
  published: { label: '현장 배포본', icon: '★', color: 'var(--sc-accent)' },
  archived: { label: '보관됨', icon: '▤', color: 'var(--one-text-muted)' },
};
